import { siteManifest } from '@/config/site';
import i18n from '@/i18n';
import type { PageMeta } from '@/types';

const withTitleSuffix = (title: string) =>
  title.includes(siteManifest.titleSuffix) ? title : `${title} | ${siteManifest.titleSuffix}`;

const createMeta = (
  overrides: Partial<PageMeta> & Pick<PageMeta, 'title' | 'description' | 'canonicalPath'>
): PageMeta => ({
  title: withTitleSuffix(overrides.title),
  description: overrides.description,
  image: overrides.image || siteManifest.defaultImage,
  canonicalPath: overrides.canonicalPath.startsWith('/') ? overrides.canonicalPath : `/${overrides.canonicalPath}`,
  robots: overrides.robots || 'index,follow',
  type: overrides.type || 'website',
  keywords: overrides.keywords || [],
  publishedTime: overrides.publishedTime,
  updatedTime: overrides.updatedTime,
});

export const getHomePageMeta = (): PageMeta =>
  createMeta({
    title: i18n.t('home.metaTitle'),
    description: i18n.t('home.metaDescription'),
    canonicalPath: '/',
  });

export const getServicesPageMeta = (): PageMeta =>
  createMeta({
    title: i18n.t('services.metaTitle'),
    description: i18n.t('services.metaDescription'),
    canonicalPath: '/services',
  });

export const getStaticPageMeta = (pathname: string): PageMeta => {
  switch (pathname) {
    case '/about':
      return createMeta({
        title: i18n.t('about.metaTitle'),
        description: i18n.t('about.metaDescription'),
        canonicalPath: pathname,
      });
    case '/services':
      return getServicesPageMeta();
    case '/contact':
      return createMeta({
        title: i18n.t('contact.metaTitle'),
        description: i18n.t('contact.metaDescription'),
        canonicalPath: pathname,
      });
    case '/portfolio':
      return createMeta({
        title: i18n.t('portfolio.metaTitle'),
        description: i18n.t('portfolio.metaDescription'),
        canonicalPath: pathname,
      });
    default:
      return getNotFoundPageMeta(pathname);
  }
};

export const getNotFoundPageMeta = (pathname: string): PageMeta =>
  createMeta({
    title: i18n.t('notFound.metaTitle'),
    description: i18n.t('notFound.metaDescription'),
    canonicalPath: pathname,
    robots: 'noindex,follow',
  });

export const getPortfolioPageMeta = (
  portfolio: { title: string; slug: string; image?: string; categories: Array<{ nameEn: string }> }
): PageMeta =>
  createMeta({
    title: i18n.t('portfolioDetail.metaTitle', { title: portfolio.title }),
    description: i18n.t('portfolioDetail.metaDescription', { title: portfolio.title }),
    canonicalPath: `/portfolio/${portfolio.slug}`,
    image: portfolio.image || siteManifest.defaultImage,
  });
